import { AnimatePresence } from 'motion/react';
import * as m from 'motion/react-m';
import { Children, PropsWithChildren, ReactNode, memo, useEffect, useMemo, useState } from 'react';

import { css } from '@flipster/styled-system/css';

interface RollingBannerItem {
  key: string;
  node: ReactNode;
}

interface RollingBannerProps extends PropsWithChildren {
  count: number;
}

export const RollingBanner = ({ count, children }: RollingBannerProps) => {
  const childrenArray = useMemo(() => Children.toArray(children), [children]);
  const [items, setItems] = useState<RollingBannerItem[]>([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => setCurrentIdx((current) => (current + 1) % childrenArray.length), 1500);
    return () => clearInterval(interval);
  }, [childrenArray]);
  useEffect(() => {
    setItems((prev) => {
      const nextItem = { key: `${currentIdx}:${Date.now()}`, node: childrenArray[currentIdx] };
      if (prev.length < count) return [...prev, nextItem];
      return [...prev.slice(1), nextItem];
    });
  }, [currentIdx, childrenArray, count]);
  return (
    <div className={css({ position: 'relative', w: '100%', h: 91, overflow: 'hidden' })}>
      <AnimatePresence>
        {items.toReversed().map(({ node, key }, stackIdx) => (
          <RollingBannerItem key={key} stackIdx={stackIdx} zIndex={count - stackIdx}>
            {node}
          </RollingBannerItem>
        ))}
      </AnimatePresence>
    </div>
  );
};

interface RollingBannerItemProps extends PropsWithChildren {
  stackIdx: number;
  zIndex: number;
}
const RollingBannerItem = memo(({ zIndex, stackIdx, children }: RollingBannerItemProps) => {
  return (
    <m.div
      className={css({
        position: 'absolute',
        width: '100%',
        bottom: 0,
      })}
      initial={{ transform: 'translateY(100%)' }}
      animate={{
        transform: `translateY(-${stackIdx * 12}px) scale(${1 - 0.05 * stackIdx})`,
        transition: { duration: 0.5 },
      }}
      exit={{ opacity: 0 }}
      style={{ zIndex }}
    >
      {children}
    </m.div>
  );
});
